/**
 * Minesweeper Engine — Core Game Logic
 *
 * Handles board creation, mine placement (after first click),
 * cell revealing with flood fill, flagging, and win detection.
 *
 * Mine placement algorithm:
 * 1. Board starts with no mines
 * 2. On first click, mines are placed using Fisher-Yates shuffle
 *    avoiding the clicked cell and its 8 neighbors
 * 3. Adjacent mine counts are calculated for all cells
 */

import {
  MinesweeperBoard,
  MinesweeperCell,
  MinesweeperGameState,
  MinesweeperDifficulty,
  DIFFICULTY_CONFIG,
} from './types';

export class MinesweeperEngine {
  private gameState: MinesweeperGameState | null = null;

  // =========================================================================
  // Public API
  // =========================================================================

  /** Initialize a new game with the given difficulty. Board has no mines yet. */
  initializeGame(difficulty: MinesweeperDifficulty): MinesweeperGameState {
    const config = DIFFICULTY_CONFIG[difficulty];
    const board = this.createEmptyBoard(config.rows, config.cols);

    this.gameState = {
      board,
      rows: config.rows,
      cols: config.cols,
      mineCount: config.mines,
      flagCount: 0,
      gameStatus: 'idle',
      timer: 0,
      firstClick: false,
      deathCell: null,
      difficulty,
      gameId: this.generateId(),
      startedAt: Date.now(),
    };

    return this.getGameState();
  }

  /** Get a deep copy of the current game state. */
  getGameState(): MinesweeperGameState {
    if (!this.gameState) {
      throw new Error('Game not initialized');
    }
    return JSON.parse(JSON.stringify(this.gameState));
  }

  /**
   * Reveal a cell. On first click, places mines avoiding this cell and neighbors.
   * If the cell is a mine, game over. If empty (0 adjacent), flood fill.
   */
  revealCell(row: number, col: number): MinesweeperGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus === 'won' || this.gameState.gameStatus === 'lost') {
      return this.getGameState();
    }

    const cell = this.gameState.board[row][col];

    // Can't reveal flagged or already revealed cells
    if (cell.isFlagged || cell.isRevealed) {
      return this.getGameState();
    }

    // First click: place mines and start the game
    if (!this.gameState.firstClick) {
      this.placeMines(row, col);
      this.gameState.firstClick = true;
      this.gameState.gameStatus = 'playing';
      this.gameState.startedAt = Date.now();
    }

    // Reveal the cell
    cell.isRevealed = true;

    // Check if it's a mine
    if (cell.isMine) {
      this.gameState.gameStatus = 'lost';
      this.gameState.deathCell = { row, col };
      this.revealAllMines();
      return this.getGameState();
    }

    // If empty (0 adjacent mines), flood fill
    if (cell.adjacentMines === 0) {
      this.floodFill(row, col);
    }

    // Check win condition
    if (this.isGameWon()) {
      this.gameState.gameStatus = 'won';
      // Auto-flag remaining mines on win
      this.autoFlagMines();
    }

    return this.getGameState();
  }

  /** Toggle flag on a cell. */
  toggleFlag(row: number, col: number): MinesweeperGameState {
    if (!this.gameState) throw new Error('Game not initialized');
    if (this.gameState.gameStatus === 'won' || this.gameState.gameStatus === 'lost') {
      return this.getGameState();
    }

    const cell = this.gameState.board[row][col];

    // Can't flag revealed cells
    if (cell.isRevealed) {
      return this.getGameState();
    }

    if (cell.isFlagged) {
      cell.isFlagged = false;
      this.gameState.flagCount--;
    } else {
      cell.isFlagged = true;
      this.gameState.flagCount++;
    }

    return this.getGameState();
  }

  /** Check if all non-mine cells are revealed. */
  isGameWon(): boolean {
    if (!this.gameState) return false;

    for (let r = 0; r < this.gameState.rows; r++) {
      for (let c = 0; c < this.gameState.cols; c++) {
        const cell = this.gameState.board[r][c];
        if (!cell.isMine && !cell.isRevealed) {
          return false;
        }
      }
    }
    return true;
  }

  /** Get the count of adjacent mines for a cell. */
  getAdjacentMines(row: number, col: number): number {
    if (!this.gameState) return 0;
    let count = 0;
    const neighbors = this.getNeighbors(row, col);
    for (const [nr, nc] of neighbors) {
      if (this.gameState.board[nr][nc].isMine) {
        count++;
      }
    }
    return count;
  }

  /** Update the timer value. */
  updateTimer(seconds: number): void {
    if (this.gameState) {
      this.gameState.timer = Math.min(seconds, 999);
    }
  }

  /** Serialize game state for persistence. */
  serialize(): string {
    return JSON.stringify(this.gameState);
  }

  /** Deserialize and restore game state. */
  deserialize(data: string): MinesweeperGameState {
    this.gameState = JSON.parse(data);
    return this.getGameState();
  }

  // =========================================================================
  // Mine Placement
  // =========================================================================

  /**
   * Place mines on the board, avoiding the first-clicked cell and its 8 neighbors.
   * Uses Fisher-Yates shuffle for random placement.
   */
  private placeMines(excludeRow: number, excludeCol: number): void {
    if (!this.gameState) return;

    const { rows, cols, mineCount } = this.gameState;

    // Build set of excluded positions (first click + neighbors)
    const excluded = new Set<string>();
    excluded.add(`${excludeRow},${excludeCol}`);
    const neighbors = this.getNeighbors(excludeRow, excludeCol);
    for (const [nr, nc] of neighbors) {
      excluded.add(`${nr},${nc}`);
    }

    // Build list of all valid positions
    const positions: [number, number][] = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!excluded.has(`${r},${c}`)) {
          positions.push([r, c]);
        }
      }
    }

    // Fisher-Yates shuffle and take first mineCount positions
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }

    const minePositions = positions.slice(0, mineCount);

    // Place mines
    for (const [r, c] of minePositions) {
      this.gameState.board[r][c].isMine = true;
    }

    // Calculate adjacent mine counts for all cells
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (!this.gameState.board[r][c].isMine) {
          this.gameState.board[r][c].adjacentMines = this.getAdjacentMines(r, c);
        }
      }
    }
  }

  // =========================================================================
  // Flood Fill
  // =========================================================================

  /**
   * Reveal connected empty cells starting from (row, col).
   * When a cell with 0 adjacent mines is revealed, recursively reveal its neighbors.
   */
  private floodFill(row: number, col: number): void {
    if (!this.gameState) return;

    const neighbors = this.getNeighbors(row, col);
    for (const [nr, nc] of neighbors) {
      const neighbor = this.gameState.board[nr][nc];
      if (!neighbor.isRevealed && !neighbor.isFlagged && !neighbor.isMine) {
        neighbor.isRevealed = true;
        if (neighbor.adjacentMines === 0) {
          this.floodFill(nr, nc);
        }
      }
    }
  }

  // =========================================================================
  // Helpers
  // =========================================================================

  /** Create an empty board with no mines. */
  private createEmptyBoard(rows: number, cols: number): MinesweeperBoard {
    const board: MinesweeperBoard = [];
    for (let r = 0; r < rows; r++) {
      const row: MinesweeperCell[] = [];
      for (let c = 0; c < cols; c++) {
        row.push({
          isMine: false,
          isRevealed: false,
          isFlagged: false,
          adjacentMines: 0,
        });
      }
      board.push(row);
    }
    return board;
  }

  /** Get valid neighbor positions for a cell. */
  private getNeighbors(row: number, col: number): [number, number][] {
    if (!this.gameState) return [];
    const { rows, cols } = this.gameState;
    const neighbors: [number, number][] = [];

    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        if (dr === 0 && dc === 0) continue;
        const nr = row + dr;
        const nc = col + dc;
        if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
          neighbors.push([nr, nc]);
        }
      }
    }

    return neighbors;
  }

  /** Reveal all mines (called on game over). */
  private revealAllMines(): void {
    if (!this.gameState) return;

    for (let r = 0; r < this.gameState.rows; r++) {
      for (let c = 0; c < this.gameState.cols; c++) {
        const cell = this.gameState.board[r][c];
        if (cell.isMine) {
          cell.isRevealed = true;
        }
      }
    }
  }

  /** Auto-flag all unflagged mines (called on win). */
  private autoFlagMines(): void {
    if (!this.gameState) return;

    for (let r = 0; r < this.gameState.rows; r++) {
      for (let c = 0; c < this.gameState.cols; c++) {
        const cell = this.gameState.board[r][c];
        if (cell.isMine && !cell.isFlagged) {
          cell.isFlagged = true;
          this.gameState.flagCount++;
        }
      }
    }
  }

  private generateId(): string {
    return `minesweeper_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
  }
}
